import type { IconName } from "@salestrak/ui";

import type { IssueAgeStatus } from "#app/lib/data";
import { PROPERTY_ICON } from "#app/lib/constants/property-icons";

/**
 * The three server age states plus `disabled` — a client-only sentinel for a
 * stage with no age config, which the server reports as an absent `age` rather
 * than as a fourth status. Anything that reads a status off the wire is an
 * `IssueAgeStatus`; only pickers and legends that offer "turn age off" widen
 * to this.
 */
export type AgeStatus = IssueAgeStatus | "disabled";

/** Status names as values, for computed keys and switch arms. `satisfies`
 *  makes tsc reject this list the moment the server set changes. */
export const AGE_STATUS = {
  onTrack: "onTrack",
  atRisk: "atRisk",
  offTrack: "offTrack",
  disabled: "disabled",
} as const satisfies Record<AgeStatus, AgeStatus>;

/** True when the Project's Update clock is running — a real status, not the
 *  updates-off state (an absent `age` from the API, or the `disabled`
 *  sentinel). The ONE definition of "has a live clock", so the board chip and
 *  the updates-off ghost can never both claim the slot. */
export function hasActiveAge(
  age?: { status?: string | null } | null,
): boolean {
  return !!age?.status && age.status !== AGE_STATUS.disabled;
}

/**
 * The vocabulary every surface reads a status through: the badge on the Follow
 * ups card, the board chip, the group band, the filter option, the chart
 * legend. The three words name positions on the Follow up CLOCK — how close
 * the next Update is — never a calendar date: On track while the cycle has
 * room, Update soon for the last 15% of the cycle (a heads-up window sized to
 * the cycle: about four days on a monthly cadence, a day on a weekly one),
 * Stale once the cycle ran out without an Update. "Update soon" names the
 * button the rep presses to reset the clock; "Stale" is the Project's
 * condition, not a missed appointment. "Due"/"Overdue" are reserved for the
 * Contact queue, which buckets the same clock by calendar distance (Due today,
 * This week), so the two vocabularies cannot be mistaken for each other. The
 * keys keep the server's names.
 *
 * Only the attention tiers carry a hue. On track is the resting state of a
 * healthy clock, and a clock that needs nothing reads at the same weight as
 * the other quiet facts around it — green would make every on-track Project
 * shout. Its count is what separates it from the updates-off ghost, which
 * wears the same muted outline pulse with no count at all.
 */
export const AGE_STATUS_CONFIG: Record<AgeStatus, {
  label: string;
  colorToken: string;
  decoColor: "green" | "yellow" | "red" | "muted";
  background: "success" | "warning" | "destructive" | "muted";
  /** The pulse glyph a status-tinted readout wears. Stale is the one
   *  solid pulse: red alone is the hue an ordinary date field also paints, so
   *  the filled mark is what says "this clock has run out" at a glance,
   *  before the color resolves. Every other state keeps the outline. */
  icon: IconName;
}> = {
  // On track's green rides the GLYPH alone — the chips read the count on the
  // muted ink (Spencer, 2026-09-14 / 2026-09-15) — so a healthy clock still
  // says "alive" without a board of green numbers.
  onTrack: { label: "On track", colorToken: "success", decoColor: "green", background: "success", icon: PROPERTY_ICON.followUp },
  atRisk: { label: "Update soon", colorToken: "warning", decoColor: "yellow", background: "warning", icon: PROPERTY_ICON.followUp },
  offTrack: { label: "Stale", colorToken: "destructive", decoColor: "red", background: "destructive", icon: "heart-pulse-filled" },
  // "muted", not deco grey — no clock is an absence, not a status hue, and
  // its glyph must sit at the same weight as the card's other ghost actions
  // (the comment button's muted icon). "No Follow up" is the one name for
  // that absence wherever a bucket needs one (filter, group band, legend) —
  // the noun the section's "Start Follow up" row answers, never "Updates
  // off", which named the mechanism.
  disabled: { label: "No Follow up", colorToken: "muted-foreground", decoColor: "muted", background: "muted", icon: PROPERTY_ICON.followUp },
};
